import React, { useEffect, useMemo, useRef } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import imageUrlBuilder from '@sanity/image-url';
import { useRouter } from 'next/router';

import useView from '@tmap/mmm-core/redux/views/useView';
import { fromKey, types } from '@tmap/mmm-core/entityKey';
import nestGet from '@tmap/mmm-core/nestGet';
import { Button } from '@tmap/mmm-style-guide/src/Button';
import { Grid } from '@tmap/mmm-style-guide/src/Grid';
import {
  ArrowForward, GradientWand,
} from '@tmap/mmm-style-guide/src/Icon';
import { styled } from '@tmap/mmm-style-guide/src/styled';
import { Typography } from '@tmap/mmm-style-guide/src/Typography';

import IncentiveCard from '../../components/incentiveCard';
import CardAd from '../../components/cardAd';
import ListCommunityBlock from '../../components/homepage/listCommunityBlock';
import Metatags from '../../components/metatags';
import OffersHeader from '../../components/offers/offersHeader';
import TagFilterBar from '../../components/tagFilterBar';

import { getIncentives, filterIncentives } from '../../actions/incentive';
import client from '../../client';
import useCurrentUser from '../../hooks/useCurrentUser';
import ColorBlocks from '../../layouts/colorBlocks';
import { getRandomEntry, shuffle } from '../../lib/helpers';
import getPromotedIncentives from '../../lib/incentive/getPromotedIncentives';
import { tagCopy } from '../../lib/tags';
import { wrapper } from '../../store';
import setCacheHeaders from '../../lib/setCacheHeaders';
import { offerFilters, offerSorts } from '../../lib/filterTypes';
import FilterBar from '../../components/filterBar';
import addObjectParamsToPath from '../../lib/addObjectParamsToPath';
import OfferProgressContext from '../../components/incentiveCard/offerProgressContext';
import RecommendedProgramsList, { GradientText } from '../../components/programList/recommendedProgramsList';
import { getRecommendedProgramsByPreferences } from '../../actions/user';

const builder = imageUrlBuilder(client);

const CenteredGrid = styled(Grid)(() => ({
  maxWidth: '600px',
  textAlign: 'center',
}));

const FullWidthPicture = styled('picture')(() => ({
  width: '100%',
}));

const FullWidthImg = styled('img')(() => ({
  width: '100%',
}));

const OfferBlockGrid = styled(Grid)(({ theme }) => ({
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(9),
}));

const PreferencesButton = styled(Button)(({ theme }) => ({
  [theme.breakpoints.down('md')]: {
    width: '100%',
    '& .MuiButton-root': {
      width: '100%',
    },
  },
  '& .MuiButton-root': {
    textWrap: 'nowrap',
    padding: theme.spacing(0.75, 5.75),
  },
}));

const doesQueryContainFilters = (query) => query.tag
  || query.st
  || query.ot
  || query.rz
  || query.rt
  || query.jt
  || query.rm;

const OFFERS_FOR_FIRST_PAGE = 8;

function EmptyIncentiveResults(props) {
  const { image, leadCopy } = props;
  return (
    <Grid container direction='column' alignItems='center' spacing={3}>
      <CenteredGrid item>
        <FullWidthPicture>
          <source srcSet={builder.image(image).format('webp').url()} />
          <FullWidthImg src={builder.image(image).url()} alt={image.alt} />
        </FullWidthPicture>
      </CenteredGrid>
      <Grid item xs={12}><Typography variant='h3'>{leadCopy}</Typography></Grid>
    </Grid>
  );
}

function GetPaid(props) {
  const {
    homeSettings,
    incentiveSettings,
    featuredIncentives = [],
    featuredTag = '',
    taggedIncentivesToDisplay = [],
    offerProgressSettings,
  } = props;

  const fetchedIncentives = useSelector((state) => nestGet(state, ['incentives', 'incentives']) ?? {}, shallowEqual);
  const favoriteInit = useSelector((state) => nestGet(state, ['users', 'favoriteInit']) ?? false, shallowEqual);
  const favorites = useSelector((state) => nestGet(state, ['users', 'favoriteListItems']) ?? [], shallowEqual);
  const filter = useSelector((state) => nestGet(state, ['incentives', 'filter']) ?? {}, shallowEqual);

  const headerIncentive = featuredIncentives[0];
  const otherFeatured = featuredIncentives.slice(1) || [];
  const tagHeadings = tagCopy[featuredTag] ?? tagCopy.featured;
  const user = useCurrentUser();

  const favoritesArray = Object.entries(favorites).map(([, favorite]) => favorite);

  const router = useRouter();
  const dispatch = useDispatch();
  const hasFilters = doesQueryContainFilters(router.query);

  // Display the user's top 3 favorite offers, prioritizing by active > applied
  // and most recent update date
  const sortedFavorites = useMemo(() => favoritesArray
    .filter((favorite) => favorite.status !== 'removed' && fromKey(favorite.itemKey).type === types.INCENTIVE)
    .sort((fav1, fav2) => {
      if (fav1.status !== fav2.status) {
        if (fav1.status === 'active') {
          return -1;
        } if (fav2.status === 'active') {
          return 1;
        }
      }
      return new Date(fav2.updatedAt) - new Date(fav1.updatedAt);
      // linter wants us to reference the array, not its length, which causes render loops
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [favoritesArray.length]);

  useEffect(() => {
    async function getFavorites() {
      const favsToFetch = sortedFavorites.map((f) => f.itemKey).slice(0, 3);
      if (user._id && favoriteInit) {
        dispatch(getIncentives(favsToFetch));
      }
    }
    getFavorites();
  }, [dispatch, favoriteInit, sortedFavorites, user._id]);

  // filtering is applied automatically during SSR, so we don't want to rerun on the first render
  const isFirstRender = useRef(true);
  const numFavorites = Object.values(favorites).length;
  const { tag } = router.query;
  const filterByRecommended = tag === 'recommended';
  useEffect(() => {
    if (!isFirstRender.current && !filterByRecommended) {
      dispatch(filterIncentives(router.query));
    } else {
      isFirstRender.current = false;
    }
  }, [dispatch, router.query, numFavorites, filterByRecommended]);

  // redux may be hydrated with more than just the incentives we fetch here, so filter our results
  // again based on stored favorites
  const topThreeFavoriteIds = sortedFavorites.slice(0, 3).map((f) => fromKey(f.itemKey).id);
  const favoritesToDisplay = Object.entries(fetchedIncentives).map(([, incentive]) => incentive)
    .filter((incentive) => topThreeFavoriteIds.includes(incentive._id));

  const otherIncentivesToShow = hasFilters
    ? filter.results.map((id) => fetchedIncentives[id]) : otherFeatured;

  const [recommendedPrograms, view] = useView('recommendedPrograms', getRecommendedProgramsByPreferences());

  return (
    <OfferProgressContext.Provider value={offerProgressSettings}>
      <Metatags
        title={incentiveSettings.title || 'Programs'}
        canonical={`${process.env.NEXT_PUBLIC_CANONICAL_DOMAIN}/get-paid`}
        description={incentiveSettings.metaDescription || ''}
        image={incentiveSettings.heroImage && builder.image(incentiveSettings.heroImage).url()}
      />
      <ColorBlocks>
        {headerIncentive && <OffersHeader entity={headerIncentive} leadText='Cities Paying You to Move &amp; Work Remotely' subLeadText='Find Your Next Home' />}
        <ColorBlocks.Block color='light' spacing='none' sx={{ boxShadow: '0px 1px 8px 0px rgba(0, 0, 0, 0.12)' }}>
          <Grid container sx={{ mt: 5 }}>
            <Grid item xs={12}>
              <TagFilterBar
                query={router.query}
                path='/get-paid/more'
              />
            </Grid>
          </Grid>
        </ColorBlocks.Block>
        {filter.query?.tag === 'recommended' ? (
          <RecommendedProgramsList
            incentives={recommendedPrograms}
            isLoading={view.isLoading}
          />
        ) : (
          <ColorBlocks.Block color='none' spacing='none'>
            <OfferBlockGrid container spacing={2}>
              <Grid item xs={12} sx={{ mt: 2 }}>
                <FilterBar
                  query={filter.query}
                  sorts={offerSorts}
                  path='/get-paid/more'
                  filters={offerFilters}
                  variant='extended'
                  showCountAndSort={false}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography component='div' fontWeight={700} fontSize='28px' sx={{ mb: 1, textTransform: 'capitalize' }}>
                  Browse {filter.query?.tag || 'All'} Programs
                </Typography>
                <Typography variant='body1' color='textSecondary'>
                  Explore the latest and greatest programs and incentives below
                </Typography>
              </Grid>
              {otherIncentivesToShow.length === 0 ? (
                <Grid item xs={12}>
                  <EmptyIncentiveResults {...incentiveSettings.emptyState} />
                </Grid>
              ) : (
                <>
                  {otherIncentivesToShow.map((incentive, i) => {
                    const isFirstRow = i < 2;
                    return (
                      <Grid item xs={12} sm={6} md={isFirstRow ? 6 : 4} key={`incCard-${incentive._id}`}>
                        <IncentiveCard
                          incentive={incentive}
                          shouldLazyLoad={i > 3}
                          lazyProps={{ offset: 1000 }}
                          placementId={`exploreOffersFeatured-${i}`}
                          pageId='exploreOffers'
                        />
                      </Grid>
                    );
                  })}
                  {filter.total > OFFERS_FOR_FIRST_PAGE && (
                    <Grid item xs={12} sx={{ textAlign: 'center', mt: 5 }}>
                      <Button variant='outlined' color='inverted' href={addObjectParamsToPath('/get-paid/more', router.query)}>View More Programs</Button>
                    </Grid>
                  )}
                </>
              )}
            </OfferBlockGrid>
          </ColorBlocks.Block>
        )}
        {!user?.preferences && (
          <ColorBlocks.Block color='dim' spacing='medium' sx={{ boxShadow: '0px 0px 10.6px 0px rgba(0, 0, 0, 0.07) inset' }}>
            <Grid container columnSpacing={7} rowSpacing={2} justifyContent='space-between' sx={{ mt: -1, mb: 1 }}>
              <Grid item xs={12} md={8}>
                <GradientText
                  variant='subtitle2'
                  sx={{ mb: 2 }}
                >
                  <GradientWand colors={['primary.light', 'pink.dark']} sx={{ mt: 0.25, mr: 0.5, fontSize: '16px' }} /> Unlock your custom recommendations
                </GradientText>
                <Typography fontSize='20px' fontWeight={500}>Get personalized program matches based on your interests when you answer a few quick questions.</Typography>
              </Grid>
              <Grid
                item
                xs={12}
                md={4}
                sx={{
                  display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end', mt: 2,
                }}
              >
                <PreferencesButton
                  variant='outlined'
                  size='medium'
                  color='inverted'
                  href='/questions/module/eligibility?completeAll=true&returnTo=/get-paid/more?tag=recommended'
                  disableElevation
                >Tell Us About You <ArrowForward sx={{ ml: 1 }} />
                </PreferencesButton>
              </Grid>
            </Grid>
          </ColorBlocks.Block>
        )}
        {(user._id && favoritesToDisplay.length > 0) && (
          <ColorBlocks.Block>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant='h6' component='div' sx={{ fontWeight: 600 }}>Your Previously Liked Programs</Typography>
                <Typography variant='body2' color='textSecondary'>Take another look at the programs you’ve favorited</Typography>
              </Grid>
              {favoritesToDisplay.slice(0, 3).map((incentive, i) => (
                <Grid item xs={12} sm={6} md={4} key={`incCard-${incentive._id}`}>
                  <IncentiveCard incentive={incentive} shouldLazyLoad lazyProps={{ offset: 1000 }} placementId={`exploreOffersFavorites-${i}`} pageId='exploreOffers' />
                </Grid>
              ))}
            </Grid>
          </ColorBlocks.Block>
        )}
        <ColorBlocks.Block spacing='large'>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant='h6' component='div' sx={{ fontWeight: 600 }}>{tagHeadings.label}</Typography>
              <Typography variant='body2' color='textSecondary'>{tagHeadings.description}</Typography>
            </Grid>
            {(taggedIncentivesToDisplay).map((incentive, i) => (
              <Grid item xs={12} sm={6} md={4} key={`incCard-${incentive._id}`}>
                <IncentiveCard incentive={incentive} shouldLazyLoad lazyProps={{ offset: 1000 }} placementId={`exploreOffersTagged-${tagHeadings.label}-${i}`} pageId='exploreOffers' />
              </Grid>
            ))}
          </Grid>
        </ColorBlocks.Block>
        {!user._id && (
          <ColorBlocks.Interstitial colorTop='light' colorBottom='light'>
            <CardAd
              leadCopy={incentiveSettings.notificationBlockLead}
              subleadCopy={incentiveSettings.notificationBlockSubLead}
            />
          </ColorBlocks.Interstitial>
        )}
        <ColorBlocks.Block>
          <ListCommunityBlock
            mainImage={homeSettings.communityBlock.image}
            leadCopy={homeSettings.communityBlock.leadCopy}
            subLeadCopy={homeSettings.communityBlock.subLeadCopy}
          />
        </ColorBlocks.Block>
      </ColorBlocks>
    </OfferProgressContext.Provider>
  );
}

export const getServerSideProps = wrapper.getServerSideProps((store) => async ({ res, query }) => {
  const [
    homeSettings, incentiveSettings, offerProgressSettings, featuredIncentives,
  ] = await Promise.all([
    client.fetch('*[_id == "homeSettings"][0]'),
    client.fetch('*[_id == "incentivesSettings"][0]'),
    client.fetch('*[_id == "offerProgressSettings"][0]'),
    getPromotedIncentives(12),
    store.dispatch(filterIncentives(query, OFFERS_FOR_FIRST_PAGE)),
  ]);
  const incentivesByTag = featuredIncentives.reduce(
    (map, incentive) => {
      const tags = incentive.tags?.map((tag) => tag.value);
      tags?.forEach((tag) => {
        if (map[tag]) {
          map[tag].push(incentive);
        } else {
          // eslint-disable-next-line no-param-reassign
          map[tag] = [incentive];
        }
      });
      return map;
    },
    {},
  );
  let taggedIncentivesToDisplay = [];
  let featuredTag;
  // if any tag has 3 or more incentives included, grab a random grouping of 3 to show separately
  const tagsWithIncentives = Object.entries(incentivesByTag)
    .filter(([, incentives]) => incentives.length >= 3);
  if (tagsWithIncentives.length > 0) {
    const [tag, taggedIncentives] = getRandomEntry(tagsWithIncentives);
    featuredTag = tag;
    taggedIncentivesToDisplay = shuffle(taggedIncentives).slice(0, 3);
  }
  setCacheHeaders(res);
  return {
    props: {
      homeSettings,
      incentiveSettings,
      featuredIncentives: featuredTag ? featuredIncentives
        .filter((incentive) => !taggedIncentivesToDisplay.includes(incentive)) : featuredIncentives,
      featuredTag: featuredTag ?? '',
      taggedIncentivesToDisplay,
      offerProgressSettings,
    },
  };
});

export default GetPaid;
